import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import Spinner from './Spinner.js';
import useClickOutside from './hooks/useClickOutside.js';
import { useConfig } from './hooks/useConfig.js';
import { ArrowLeft, Edit2, CheckCircle, Save, Send, X, CreditCard, ClipboardCheck, Truck, UserCheck, AlertCircle, Clock, Loader2, ChevronDown, Printer } from 'lucide-react';

const OrderApprovalDetail = ({ db, user }) => {
  const { config } = useConfig();
  const { orderId } = useParams(); // This is the MOC (dispatch) ID
  const navigate = useNavigate();
  const [moc, setMoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const printRef = useRef();

  const [showDropdown, setShowDropdown] = useState(false);

  // --- REJECTION STATE ---
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);

  // --- HOLD STATE ---
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [holdReason, setHoldReason] = useState('');
  const [otherHoldReason, setOtherHoldReason] = useState('');
  const [isHolding, setIsHolding] = useState(false);

  const dropdownRef = useClickOutside(() => setShowDropdown(false));

  useEffect(() => {
    const fetchMoc = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, "dispatches", orderId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setMoc({ id: docSnap.id, ...docSnap.data() });
        } else {
          alert("MOC not found");
          navigate('/accounts/approvals');
        }
      } catch (error) {
        console.error("Error fetching MOC:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMoc();
  }, [db, orderId, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setMoc(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveDraft = async () => {
    setIsSaving(true);
    try {
      await updateDoc(doc(db, "dispatches", orderId), {
        ...moc,
        updatedAt: serverTimestamp()
      });
      alert("Finance details saved as draft.");
      setIsEditing(false);
    } catch (error) {
      alert("Error saving: " + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleApprove = async () => {
    if (!moc.paymentTerms || !moc.outstandingAmount || !moc.approvedByFinance) {
        alert("Please fill in Payment Terms, Outstanding Amount, and Verified By before approving.");
        return;
    }

    if (!window.confirm("Approve this MOC? Status will change to 'MOC Approved'.")) return;

    setIsSaving(true);
    try {
      const timestamp = serverTimestamp();
      // 1. Update Dispatch (MOC)
      await updateDoc(doc(db, "dispatches", orderId), {
        ...moc,
        dispatchStatus: 'moc_approved',
        financeApprovedAt: timestamp,
        updatedAt: timestamp
      });

      // 2. Update Linked Order
      if (moc.orderId) {
        await updateDoc(doc(db, "orders", moc.orderId), {
          status: 'moc_approved',
          updatedAt: timestamp
        });
      }

      alert("MOC Approved successfully!");
      navigate('/accounts/approvals');
    } catch (error) {
      alert("Error approving: " + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectionReason) {
      alert("Please select a rejection reason.");
      return;
    }
    if (rejectionReason === 'Other' && !otherReason.trim()) {
      alert("Please specify the other reason.");
      return;
    }

    const finalReason = rejectionReason === 'Other' ? otherReason : rejectionReason;

    if (!window.confirm(`Are you sure you want to reject this MOC?`)) return;

    setIsRejecting(true);
    try {
      // 1. Update Dispatch (MOC)
      await updateDoc(doc(db, "dispatches", orderId), {
        dispatchStatus: 'fin_rejected',
        finRejectionReason: finalReason,
        updatedAt: serverTimestamp()
      });

      // 2. Update Linked Order
      if (moc.orderId) {
        await updateDoc(doc(db, "orders", moc.orderId), {
          status: 'fin_rejected',
          finRejectionReason: finalReason,
          updatedAt: serverTimestamp()
        });
      }

      alert("MOC rejected successfully.");
      setShowRejectModal(false);
      setRejectionReason('');
      setOtherReason('');
      navigate('/accounts/approvals');
    } catch (error) {
      alert("Error rejecting MOC: " + error.message);
    } finally {
      setIsRejecting(false);
    }
  };

  const handleHoldSubmit = async () => {
    if (!holdReason) {
      alert("Please select a hold reason.");
      return;
    }
    if (holdReason === 'Other' && !otherHoldReason.trim()) {
      alert("Please specify the other reason.");
      return;
    }

    const finalReason = holdReason === 'Other' ? otherHoldReason : holdReason;

    if (!window.confirm(`Are you sure you want to put this MOC on Hold?`)) return;

    setIsHolding(true);
    try {
      // 1. Update Dispatch (MOC)
      await updateDoc(doc(db, "dispatches", orderId), {
        dispatchStatus: 'fin_hold',
        finHoldReason: finalReason,
        updatedAt: serverTimestamp()
      });

      // 2. Update Linked Order
      if (moc.orderId) {
        await updateDoc(doc(db, "orders", moc.orderId), {
          status: 'fin_hold',
          finHoldReason: finalReason,
          updatedAt: serverTimestamp()
        });
      }

      alert("MOC put on hold successfully.");
      setShowHoldModal(false);
      setHoldReason('');
      setOtherHoldReason('');
      navigate('/accounts/approvals');
    } catch (error) {
      alert("Error putting MOC on hold: " + error.message);
    } finally {
      setIsHolding(false);
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current.innerHTML;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>MOC ${moc.mocNo}</title>
          <style>
            body { font-family: Arial, sans-serif; color: #000; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
            td, th { border: 1px solid #000 !important; padding: 8px; text-align: left; font-size: 13px; }
            th { font-weight: bold; background-color: #f2f2f2; }
            @page { margin: 1cm; }
          </style>
        </head>
        <body>${printContent}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 250);
  };

  if (loading) return <Spinner />;

  // Check if current user is finance (accounts role)
  const isFinance = user?.role === 'accounts';

  // Determine if we should show finance actions (only in initial pending/hold/rejected stages)
  const showFinanceActions = isFinance && ['moc_pending_finance', 'fin_hold', 'fin_rejected'].includes(moc?.dispatchStatus);

  // --- RENDER PREVIEW MODE (PDF Layout) ---
  const renderPreview = () => (
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
      <div ref={printRef} style={pdfContainerStyle}>
        {/* Header Table */}
        <table style={headerTableStyle}>
          <tbody>
            <tr>
              <td colSpan="5" style={{ ...headerTableCellStyle, textAlign: 'center', backgroundColor: '#f1f5f9', fontWeight: '800', fontSize: '18px' }}>
                PACKAGING/BILL OF MATERIAL
              </td>
            </tr>
            <tr>
              <td style={labelCellStyle}>Document date</td>
              <td style={valueCellStyle}>{moc.mocDate}</td>
              <td style={labelCellStyle}>Document No.</td>
              <td colSpan="2" style={valueCellStyle}>{moc.mocNo}</td>
            </tr>
            <tr>
              <td style={labelCellStyle}>Type of MOC</td>
              <td style={valueCellStyle}>{moc.typeOfMOC}</td>
              <td style={labelCellStyle}>Document type</td>
              <td style={valueCellStyle}>{moc.documentType}</td>
              <td style={{...valueCellStyle, backgroundColor: '#fdf2f2'}}><strong>ALW:</strong> {moc.aluminiumWeight} kg</td>
            </tr>
            <tr>
              <td style={labelCellStyle}>PO Number</td>
              <td style={valueCellStyle}>{moc.poNumber}</td>
              <td style={labelCellStyle}>PO DATE</td>
              <td style={valueCellStyle}>{moc.poDate}</td>
              <td style={valueCellStyle}><strong>Warehouse:</strong> {moc.warehouse}</td>
            </tr>
            <tr>
              <td style={labelCellStyle}>Consignee</td>
              <td colSpan="3" style={{...valueCellStyle, fontWeight: 'bold'}}>{moc.customerName}</td>
              <td rowSpan="4" style={{ ...headerTableCellStyle, textAlign: 'center', verticalAlign: 'middle', borderLeft: '2px solid #000', width: '120px' }}>
                <strong>Total Meter</strong><br />
                <span style={{ fontSize: '32px', fontWeight: '900' }}>{moc.totalMeterNumber}</span>
              </td>
            </tr>
            <tr>
              <td style={labelCellStyle}>Shipping Address</td>
              <td colSpan="3" style={{ ...valueCellStyle, height: '60px', verticalAlign: 'top' }}>{moc.shippingAddress}</td>
            </tr>
            <tr>
              <td style={labelCellStyle}>Material Code</td>
              <td colSpan="3" style={valueCellStyle}>{moc.materialCode}</td>
            </tr>
            <tr>
              <td style={labelCellStyle}>Product Summary</td>
              <td colSpan="3" style={valueCellStyle}>{moc.productSummary}</td>
            </tr>
          </tbody>
        </table>

        {/* Component Table */}
        <table style={pdfTableStyle}>
          <thead>
            <tr style={{ backgroundColor: '#f8fafc' }}>
              <th style={pdfThStyle}>S.No</th>
              <th style={pdfThStyle}>SKU</th>
              <th style={{ ...pdfThStyle, textAlign: 'left' }}>Item Name</th>
              <th style={pdfThStyle}>New Qty</th>
              <th style={pdfThStyle}>Rental Qty</th>
              <th style={pdfThStyle}>Total Weight</th>
            </tr>
          </thead>
          <tbody>
            {moc.components?.map((c, i) => (
              <tr key={i}>
                <td style={pdfTdStyle}>{i + 1}</td>
                <td style={pdfTdStyle}>{c.sku}</td>
                <td style={{ ...pdfTdStyle, textAlign: 'left' }}>{c.name}</td>
                <td style={pdfTdStyle}>{c.newQty || 0}</td>
                <td style={pdfTdStyle}>{c.rentalQty || 0}</td>
                <td style={pdfTdStyle}>{((parseInt(c.newQty || 0) + parseInt(c.rentalQty || 0)) * parseFloat(c.weight || 0)).toFixed(2)} kg</td>
              </tr>
            ))}
            <tr style={{ fontWeight: 'bold', backgroundColor: '#f8fafc' }}>
              <td colSpan="5" style={{ ...pdfTdStyle, textAlign: 'right' }}>TOTAL WEIGHT</td>
              <td style={pdfTdStyle}>{parseFloat(moc.totalWeight || 0).toFixed(2)} kg</td>
            </tr>
          </tbody>
        </table>

        {/* Verification Section */}
        <table style={{ ...headerTableStyle, marginTop: '20px' }}>
          <tbody>
            <tr>
              <td style={labelCellStyle}>Prepared by</td><td style={valueCellStyle}>{moc.preparedBy}</td>
              <td rowSpan="6" style={{ ...headerTableCellStyle, verticalAlign: 'top', fontSize: '11px', width: '45%', borderLeft: '2px solid #000' }}>
                <div style={{fontWeight: 'bold', marginBottom: '5px', textDecoration: 'underline'}}>NOTICE FOR SALES TEAM:</div>
                Sales Team, please verify the Material Quantity Against your BOM Before Signing the MOC. Any Discrepancies in the MOC Will Not Be the Responsibility of the Operations Team, As It Has Been Prepared Exactly As Per Your BOM.
              </td>
            </tr>
            <tr><td style={labelCellStyle}>Qty Verified By (Approval)</td><td style={valueCellStyle}>{moc.quantityVerifiedBy}</td></tr>
            <tr><td style={labelCellStyle}>Qty Verified By (Dispatch)</td><td style={valueCellStyle}>{moc.quantityVerifiedByDispatch}</td></tr>
            <tr><td style={labelCellStyle}>Vehicle Number</td><td style={valueCellStyle}>{moc.vehicleNumber}</td></tr>
            <tr><td style={labelCellStyle}>Vehicle In Time</td><td style={valueCellStyle}>{moc.vehicleInTime}</td></tr>
            <tr><td style={labelCellStyle}>Vehicle Out Time</td><td style={valueCellStyle}>{moc.vehicleOutTime}</td></tr>
            <tr><td style={labelCellStyle}>Date</td><td style={valueCellStyle}>{moc.vehicleDate}</td><td style={{ ...headerTableCellStyle, textAlign: 'center', fontWeight: 'bold', fontSize: '14px', backgroundColor: '#f2f2f2' }}>SIGNATURE</td></tr>
          </tbody>
        </table>

        {/* Finance Section Preview */}
        <table style={{ ...headerTableStyle, marginTop: '20px' }}>
          <tbody>
            <tr>
              <td colSpan="4" style={{ ...headerTableCellStyle, textAlign: 'center', backgroundColor: '#f1f5f9', fontWeight: '800' }}>
                FOR FINANCE USE ONLY
              </td>
            </tr>
            <tr>
              <td style={{...labelCellStyle, width: '25%'}}>Payment Terms</td>
              <td style={{...valueCellStyle, width: '25%'}}>{moc.paymentTerms}</td>
              <td style={{...labelCellStyle, width: '25%'}}>Payment Status</td>
              <td style={{...valueCellStyle, width: '25%'}}>{moc.paymentStatus}</td>
            </tr>
            <tr>
              <td style={labelCellStyle}>Last Payment amount</td>
              <td style={valueCellStyle}>{moc.lastPaymentAmount}</td>
              <td style={labelCellStyle}>Outstanding Amount</td>
              <td style={valueCellStyle}>{moc.outstandingAmount}</td>
            </tr>
            <tr>
              <td style={labelCellStyle}>Payment Rec. Date</td>
              <td style={valueCellStyle}>{moc.paymentRecDate}</td>
              <td style={labelCellStyle}>Outstanding days</td>
              <td style={valueCellStyle}>{moc.outstandingDays}</td>
            </tr>
            <tr>
              <td style={labelCellStyle}>Sign/Name (Finance)</td>
              <td style={valueCellStyle}>{moc.approvedByFinance}</td>
              <td style={labelCellStyle}>Salesperson sign/name</td>
              <td style={valueCellStyle}>{moc.approvedBySalesperson}</td>
            </tr>
          </tbody>
        </table>

        {/* Signature Area */}
        <div style={{ marginTop: '100px', display: 'flex', justifyContent: 'space-between', padding: '0 20px', alignItems: 'flex-end' }}>
          <div style={{ ...signLineStyle, position: 'relative' }}>
            {moc.approvedBySalesperson && (
              ['Rohit Soni', 'Rajesh Soni', 'Sanjay Kumawat'].includes(moc.approvedBySalesperson) ? (
                <>
                  {moc.approvedBySalesperson === 'Rohit Soni' && <img src="/Rohit-Soni-Sign.png" alt="Sign" style={signImageOverlayStyle} />}
                  {moc.approvedBySalesperson === 'Rajesh Soni' && <img src="/Rajesh-Soni-Sign.png" alt="Sign" style={signImageOverlayStyle} />}
                  {moc.approvedBySalesperson === 'Sanjay Kumawat' && <img src="/Sanjay-Kumawat-Sign.png" alt="Sign" style={signImageOverlayStyle} />}
                </>
              ) : (
                <img src="/placeholder-sign.png" alt="Sign" style={{ ...signImageOverlayStyle, maxHeight: '50px', bottom: '35px' }} />
              )
            )}
            Salesperson
          </div>
          <div style={{ ...signLineStyle, position: 'relative' }}>
            {moc.approvedByFinance === 'Jasveer Singh' && (
              <img src="/Jasveer-sir-Sign.png" alt="Sign" style={signImageOverlayStyle} />
            )}
            {(moc.approvedByFinance === 'Tikkam' || moc.approvedByFinance === 'Tikkam Chand') && (
              <img src="/Tikkam-Sign.png" alt="Sign" style={signImageOverlayStyle} />
            )}
            {(moc.approvedByFinance === 'Saurabh' || moc.approvedByFinance === 'Saurabh Ganeriwal') && (
              <img src="/Saurabh-Ganeriwal-Sign.png" alt="Sign" style={signImageOverlayStyle} />
            )}
            For Finance Approval
          </div>
          <div style={{ ...signLineStyle, position: 'relative' }}>
            {moc.quantityVerifiedBy === 'Ghanshyam Prajapat' && (
              <img src="/Ghansyam-Sign.png" alt="Sign" style={signImageOverlayStyle} />
            )}
            {moc.quantityVerifiedBy === 'Rajesh Kumar' && (
              <img src="/Rajesh-Kumar-Sign.png" alt="Sign" style={signImageOverlayStyle} />
            )}
            Operations Head
          </div>
        </div>
      </div>
    </div>
  );

  // --- RENDER EDIT MODE (Form Layout) ---
  const renderEdit = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Card 1: Document Details (ReadOnly) */}
        <div style={cardStyle}>
          <h3 style={sectionHeaderStyle}>Document Details (Read-Only)</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div><label style={labelStyle}>MOC No.</label><input type="text" value={moc.mocNo} style={{...inputStyle, backgroundColor: '#f8fafc'}} disabled /></div>
            <div><label style={labelStyle}>MOC Date</label><input type="text" value={moc.mocDate} style={{...inputStyle, backgroundColor: '#f8fafc'}} disabled /></div>
            <div style={{ gridColumn: 'span 2' }}><label style={labelStyle}>Customer</label><input type="text" value={moc.customerName} style={{...inputStyle, backgroundColor: '#f8fafc'}} disabled /></div>
          </div>
        </div>

        {/* Card 2: Location (ReadOnly) */}
        <div style={cardStyle}>
          <h3 style={sectionHeaderStyle}>Location & Logistics (Read-Only)</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div><label style={labelStyle}>Warehouse</label><input type="text" value={moc.warehouse} style={{...inputStyle, backgroundColor: '#f8fafc'}} disabled /></div>
            <div><label style={labelStyle}>Shipping Address</label><textarea value={moc.shippingAddress} style={{...inputStyle, backgroundColor: '#f8fafc', minHeight: '60px'}} disabled /></div>
          </div>
        </div>
      </div>

      {/* Card: BOM Summary (Read-Only) */}
      <div style={cardStyle}>
        <h3 style={sectionHeaderStyle}>BOM / Components (Read-Only)</h3>
        <table style={tableStyle}>
            <thead>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                    <th style={thStyle}>S.No.</th>
                    <th style={thStyle}>SKU</th>
                    <th style={{ ...thStyle, textAlign: 'left' }}>Component Name</th>
                    <th style={thStyle}>New Qty</th>
                    <th style={thStyle}>Rental Qty</th>
                    <th style={thStyle}>Weight</th>
                </tr>
            </thead>
            <tbody>
                {moc.components?.map((c, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={tdStyle}>{idx + 1}</td>
                        <td style={tdStyle}>{c.sku}</td>
                        <td style={{ ...tdStyle, textAlign: 'left' }}>{c.name}</td>
                        <td style={tdStyle}>{c.newQty || 0}</td>
                        <td style={tdStyle}>{c.rentalQty || 0}</td>
                        <td style={tdStyle}>{c.weight} kg</td>
                    </tr>
                ))}
                <tr style={{ backgroundColor: '#f9fafb', fontWeight: 'bold' }}>
                    <td colSpan="5" style={{ ...tdStyle, textAlign: 'right' }}>TOTAL WEIGHT</td>
                    <td style={tdStyle}>{moc.totalWeight?.toFixed(2)} kg</td>
                </tr>
            </tbody>
        </table>
      </div>

      {/* Card 3: Finance Details (EDITABLE) */}
      <div style={{ ...cardStyle, border: '2px solid #2563eb' }}>
        <h3 style={{ ...sectionHeaderStyle, color: '#2563eb' }}><CreditCard size={18} /> Finance Details (Editable)</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
            <div style={{ gridColumn: 'span 2' }}>
                <label style={labelStyle}>Payment Terms</label>
                <input name="paymentTerms" value={moc.paymentTerms} onChange={handleChange} style={inputStyle} placeholder="e.g. 30 Days Credit" />
            </div>
            <div>
                <label style={labelStyle}>Payment Status</label>
                <select name="paymentStatus" value={moc.paymentStatus} onChange={handleChange} style={inputStyle}>
                    <option value="">Select Status...</option>
                    <option value="Received">Received</option>
                    <option value="Credit">Credit</option>
                    <option value="Old Payment Received">Old Payment Received</option>
                </select>
            </div>
            <div><label style={labelStyle}>Last Payment Amount</label><input type="number" name="lastPaymentAmount" value={moc.lastPaymentAmount} onChange={handleChange} style={inputStyle} /></div>
            <div><label style={labelStyle}>Payment Rec. Date</label><input type="date" name="paymentRecDate" value={moc.paymentRecDate} onChange={handleChange} style={inputStyle} /></div>
            <div><label style={labelStyle}>Outstanding Amount</label><input type="number" name="outstandingAmount" value={moc.outstandingAmount} onChange={handleChange} style={inputStyle} /></div>
            <div><label style={labelStyle}>Outstanding Days</label><input type="number" name="outstandingDays" value={moc.outstandingDays} onChange={handleChange} style={inputStyle} /></div>
            <div style={{ gridColumn: 'span 2' }}>
                <label style={labelStyle}>Verified By (Finance)</label>
                <select name="approvedByFinance" value={moc.approvedByFinance} onChange={handleChange} style={inputStyle}>
                    <option value="">Select Staff...</option>
                    {config.finance_verified_by.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
            </div>
            <div style={{ gridColumn: 'span 3' }}>
                <label style={labelStyle}>Finance Remarks</label>
                <textarea name="financeRemarks" value={moc.financeRemarks} onChange={handleChange} style={{ ...inputStyle, minHeight: '80px' }} placeholder="Internal notes..." />
            </div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      {/* Dynamic Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '25px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={() => navigate('/accounts/approvals')} style={backBtnStyle}>
            <ArrowLeft size={24} />
          </button>
          <h2 style={{ margin: 0 }}>{isEditing ? 'Edit Finance Details' : (!showFinanceActions ? `MOC Record: ${moc?.mocNo || ''}` : `MOC Approval Preview: ${moc?.mocNo || ''}`)}</h2>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
            {showFinanceActions ? (
                isEditing ? (
                    <>
                        <button onClick={() => setIsEditing(false)} style={secondaryBtnStyle}>Cancel</button>
                        <button onClick={handleSaveDraft} disabled={isSaving} style={draftBtnStyle}><Save size={18}/> Save Draft</button>
                        <button onClick={handleApprove} disabled={isSaving} style={confirmBtnStyle}><Send size={18}/> Approve MOC</button>
                    </>
                ) : (
                    <>
                        <button onClick={() => setIsEditing(true)} style={editBtnStyle}><Edit2 size={18}/> Edit Finance Details</button>
                        <button onClick={handleApprove} disabled={isSaving} style={confirmBtnStyle}><CheckCircle size={18}/> Approve MOC</button>
                        <div style={{ position: 'relative' }} ref={dropdownRef}>
                          <button onClick={() => setShowDropdown(!showDropdown)} style={secondaryBtnStyle}>
                            More <ChevronDown size={18} />
                          </button>
                          {showDropdown && (
                            <div style={{
                              position: 'absolute',
                              top: 'calc(100% + 5px)',
                              right: 0,
                              backgroundColor: 'white',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                              width: '150px',
                              zIndex: 1000
                            }}>
                              <div onClick={() => { setShowRejectModal(true); setShowDropdown(false); }} style={{ padding: '10px 15px', cursor: 'pointer', color: '#ef4444', fontWeight: 'bold', borderBottom: '1px solid #f3f4f6' }}>
                                Reject MOC
                              </div>
                              <div onClick={() => { setShowHoldModal(true); setShowDropdown(false); }} style={{ padding: '10px 15px', cursor: 'pointer', color: '#f59e0b', fontWeight: 'bold' }}>
                                Hold MOC
                              </div>
                            </div>
                          )}
                        </div>
                    </>
                )
            ) : ( 
                <button onClick={handlePrint} style={confirmBtnStyle}><Printer size={18}/> Export PDF</button>
            )}
        </div>
      </div>

      {isEditing ? renderEdit() : renderPreview()}

      {/* REJECTION MODAL */}
      {showRejectModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', width: '400px', maxWidth: '90%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#dc2626' }}>Reject MOC</h3>
              <button onClick={() => { setShowRejectModal(false); setRejectionReason(''); setOtherReason(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            
            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '15px' }}>
              Please select a reason for rejecting this MOC. It will be sent back to the Sales team.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
              {["Pending Payment", "Credit Limit Exceeded", "Document Missing", "Other"].map((reason) => (
                <label key={reason} style={radioLabelStyle}>
                  <input 
                    type="radio" 
                    name="rejectionReason" 
                    value={reason} 
                    checked={rejectionReason === reason} 
                    onChange={(e) => setRejectionReason(e.target.value)} 
                  />
                  {reason}
                </label>
              ))}
              
              {rejectionReason === 'Other' && (
                <textarea 
                  placeholder="Please specify the reason..." 
                  value={otherReason} 
                  onChange={(e) => setOtherReason(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', minHeight: '80px', marginTop: '10px', boxSizing: 'border-box' }}
                />
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => { setShowRejectModal(false); setRejectionReason(''); setOtherReason(''); }} style={draftBtnStyle}>Cancel</button>
              <button onClick={handleRejectSubmit} disabled={isRejecting} style={{...confirmBtnStyle, backgroundColor: '#dc2626'}}>
                {isRejecting ? <Loader2 className="animate-spin" size={16} /> : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HOLD MODAL */}
      {showHoldModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', width: '400px', maxWidth: '90%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#f59e0b' }}>Hold MOC</h3>
              <button onClick={() => { setShowHoldModal(false); setHoldReason(''); setOtherHoldReason(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            
            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '15px' }}>
              Please select a reason for placing this MOC on hold.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
              {["Awaiting Payment Confirmation", "Pending Document Upload", "Other"].map((reason) => (
                <label key={reason} style={radioLabelStyle}>
                  <input 
                    type="radio" 
                    name="holdReason" 
                    value={reason} 
                    checked={holdReason === reason} 
                    onChange={(e) => setHoldReason(e.target.value)} 
                  />
                  {reason}
                </label>
              ))}
              
              {holdReason === 'Other' && (
                <textarea 
                  placeholder="Please specify the hold reason..." 
                  value={otherHoldReason} 
                  onChange={(e) => setOtherHoldReason(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', minHeight: '80px', marginTop: '10px', boxSizing: 'border-box' }}
                />
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => { setShowHoldModal(false); setHoldReason(''); setOtherHoldReason(''); }} style={draftBtnStyle}>Cancel</button>
              <button onClick={handleHoldSubmit} disabled={isHolding} style={{...confirmBtnStyle, backgroundColor: '#f59e0b'}}>
                {isHolding ? <Loader2 className="animate-spin" size={16} /> : 'Confirm Hold'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- STYLES ---
const cardStyle = { backgroundColor: 'white', padding: '20px', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
const sectionHeaderStyle = { fontSize: '16px', fontWeight: 'bold', borderBottom: '1px solid #f3f4f6', paddingBottom: '10px', marginBottom: '15px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' };
const labelStyle = { display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' };
const inputStyle = { width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' };
const radioLabelStyle = { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' };
const btnStyle = { padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', border: 'none' };
const confirmBtnStyle = { ...btnStyle, backgroundColor: '#059669', color: 'white' };
const editBtnStyle = { ...btnStyle, backgroundColor: '#2563eb', color: 'white' };
const draftBtnStyle = { ...btnStyle, backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db' };
const secondaryBtnStyle = { ...btnStyle, backgroundColor: 'white', color: '#64748b', border: '1px solid #e2e8f0' };
const backBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' };

const pdfContainerStyle = { backgroundColor: 'white', padding: '40px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', minHeight: '800px', color: '#000' };
const headerTableStyle = { width: '100%', borderCollapse: 'collapse', marginBottom: '15px', border: '1px solid #000' };
const headerTableCellStyle = { border: '1px solid #000', padding: '8px', fontSize: '13px' };
const labelCellStyle = { ...headerTableCellStyle, fontWeight: 'bold', backgroundColor: '#f2f2f2' };
const valueCellStyle = { ...headerTableCellStyle, backgroundColor: '#fff' };
const pdfTableStyle = { width: '100%', borderCollapse: 'collapse', marginTop: '15px', border: '1px solid #000' };
const pdfThStyle = { border: '1px solid #000', padding: '6px', fontSize: '12px', textAlign: 'center', fontWeight: 'bold' };
const pdfTdStyle = { border: '1px solid #000', padding: '6px', fontSize: '12px', textAlign: 'center' };
const signLineStyle = { borderTop: '1px solid #000', width: '180px', textAlign: 'center', paddingTop: '10px', fontSize: '12px', fontWeight: 'bold' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', marginTop: '10px' };
const thStyle = { padding: '12px 10px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #f3f4f6' };
const tdStyle = { padding: '12px 10px', fontSize: '13px', color: '#374151', textAlign: 'center' };
const signImageOverlayStyle = { 
  position: 'absolute', 
  bottom: '30px', 
  left: '50%', 
  transform: 'translateX(-50%)', 
  maxHeight: '100px',
  pointerEvents: 'none'
};

export default OrderApprovalDetail;